import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { uniqBy, without } from 'lodash';
import { PlusIcon } from '@heroicons/react/20/solid';
import { getShipment, updateShipmentStopEquipment, } from 'utils/ShipmentWebAPIUtils';
import AddEquipmentForm from 'components/modules/equipment/AddEquipmentForm';
import LoadingCircle from 'components/ui/LoadingCircle';
import Tooltip from 'components/v2/Tooltip';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import EquipmentStore from 'stores/EquipmentStore';
import { containerExists, findContainer, findTrailer, getAvailableEquipment, getUnmappedAccessorialRequests, trailerExists, } from 'helpers/equipment';
import { shipperHasTag } from 'utils/formatters/shipments';
import { checkEquipmentExists } from 'utils/EquipmentWebAPIUtils';
import { sluggify } from 'utils/localization/utils';
import Alert from '../../../ui/Alert';
import EquipmentTable from '../EquipmentTable';
import EquipmentSearch from '../../orders/equipments/EquipmentSearch';
import OrphanAdjustments from '../load/OrphanAdjustments';
import CargomaticModal from '../../../v2/modal';
import { isPrimaryStopForShipment } from 'helpers/stops';
import { getAccessorialRequests } from 'utils/AccessorialRequestWebAPIUtils';
const getInitialState = () => {
    return {
        success: false,
        error: null,
        showAddNewEquipmentForm: false,
        availableEquipment: [],
        equipments: [],
        equipmentSearchText: '',
        selectedReceivables: [],
        selectedPayables: [],
        selectedUnmappedAccessorialRequests: [],
        addNewEquipmentError: null,
        unmappedAccessorialRequests: [],
    };
};
function StopEquipmentModalTitle() {
    const { t } = useTranslation();
    return (React.createElement("div", { className: "flex items-center space-x-1" },
        React.createElement("h4", { className: "my-0" }, t('STOP_EQUIPMENT')),
        React.createElement(Tooltip, { id: "select-equipment" }, t('STOP_EQUIPMENT_NOTE'))));
}
function StopEquipmentModalFooter({ cancelDisabled, saveDisabled, onCancel, onSave, }) {
    const { t } = useTranslation();
    return (React.createElement("div", { className: "flex justify-end button-group" },
        React.createElement("button", { className: "block button secondary-action", disabled: cancelDisabled, onClick: onCancel }, t('CANCEL')),
        React.createElement("button", { className: "block mr-2 button button-green", onClick: onSave, disabled: saveDisabled }, titleize(t('SAVE')))));
}
class StopEquipmentModal extends Component {
    constructor(props) {
        super(props);
        this.fetchShipment = async () => {
            var _a, _b, _c;
            const { shipmentId, stopId } = this.props;
            try {
                const [shipmentResult, shipmentAccessorialResult] = await Promise.all([
                    getShipment(shipmentId),
                    getAccessorialRequests({
                        shipment: shipmentId,
                    }),
                ]);
                const shipment = ((_a = shipmentResult === null || shipmentResult === void 0 ? void 0 : shipmentResult.entity) === null || _a === void 0 ? void 0 : _a.item) || {};
                let stops = ((_b = shipment === null || shipment === void 0 ? void 0 : shipment.stops) === null || _b === void 0 ? void 0 : _b.map((stop) => (Object.assign(Object.assign({}, stop), { isPrimary: !!isPrimaryStopForShipment(stop, shipment) })))) || [];
                const isPrimaryStopAvailable = stops.some((stop) => stop.isPrimary);
                if (!isPrimaryStopAvailable) {
                    stops = stops.map((stop) => {
                        return Object.assign(Object.assign({}, stop), { isPrimary: stop.sequence === 1 && !stop.off_site });
                    });
                }
                const stop = stops.find((s) => s._id === stopId);
                const equipments = uniqBy(((stop === null || stop === void 0 ? void 0 : stop.equipments) || []).map((eq) => (Object.assign(Object.assign({}, eq), { canRemove: true }))), (eq) => eq._id);
                let selectedReceivables = [];
                let selectedPayables = [];
                let selectedUnmappedAccessorialRequests = [];
                const availableEquipment = getAvailableEquipment(shipment, equipments);
                const unmappedAccessorialRequests = getUnmappedAccessorialRequests(shipment, (_c = shipmentAccessorialResult === null || shipmentAccessorialResult === void 0 ? void 0 : shipmentAccessorialResult.data) === null || _c === void 0 ? void 0 : _c.request);
                if (stop === null || stop === void 0 ? void 0 : stop.isPrimary) {
                    selectedReceivables = shipment.receivables
                        .filter((adj) => !(adj === null || adj === void 0 ? void 0 : adj.equipment))
                        .map((r) => r._id);
                    selectedPayables = shipment.payables
                        .filter((adj) => !(adj === null || adj === void 0 ? void 0 : adj.equipment))
                        .map((p) => p._id);
                    selectedUnmappedAccessorialRequests = unmappedAccessorialRequests.map((item) => item._id);
                }
                const hasDrayageTrailerMovesTag = shipperHasTag(shipment, 'drayage_trailer_moves', shipment.shipment_type === 'drayage');
                this.setState({
                    shipment,
                    stop,
                    availableEquipment,
                    equipments,
                    loaded: true,
                    saving: false,
                    selectedReceivables,
                    selectedPayables,
                    hasDrayageTrailerMovesTag,
                    unmappedAccessorialRequests,
                    selectedUnmappedAccessorialRequests,
                });
            }
            catch (e) {
                console.error(e);
            }
        };
        this.getCreatedEquipment = () => {
            const equipment = EquipmentStore.getCreatedEquipment();
            this.handleEquipmentSearchSelect(equipment);
            this.setState({ showAddNewEquipmentForm: false });
        };
        this.handleToggleOrphanedAdjustment = (type, item, checked) => {
            const typeToStateKey = {
                receivables: 'selectedReceivables',
                payables: 'selectedPayables',
                accessorials: 'selectedUnmappedAccessorialRequests',
            };
            if (!typeToStateKey[type])
                return;
            this.setState((prevState) => ({
                [typeToStateKey[type]]: this.handleSelectionChange(item, checked, prevState[typeToStateKey[type]]),
            }));
        };
        this.handleSelectionChange = (item, checked, list) => {
            if (checked) {
                list.push(item._id);
            }
            else {
                list = without(list, item._id);
            }
            return list;
        };
        this.handleEquipmentRemove = (equipment) => {
            this.setState((prevState) => {
                const equipments = prevState.equipments.filter((eq) => eq._id !== equipment._id);
                const availableEquipment = uniqBy([...prevState.availableEquipment, Object.assign(Object.assign({}, equipment), { canAdd: true })], (eq) => eq._id);
                return {
                    equipments,
                    availableEquipment,
                };
            });
        };
        // Add user equipment
        this.handleEquipmentSelect = (equipment) => {
            this.setState((prevState) => {
                const equipments = uniqBy([...prevState.equipments, Object.assign(Object.assign({}, equipment), { canRemove: true })], (eq) => eq._id);
                return {
                    equipments,
                    availableEquipment: prevState.availableEquipment
                        .filter((eq) => eq._id !== equipment._id)
                        .map((eq) => (Object.assign(Object.assign({}, eq), { canAdd: true }))),
                };
            });
        };
        this.handleEquipmentSearchSelect = (equipment) => {
            this.setState((prevState) => {
                const equipments = uniqBy([...prevState.equipments, Object.assign(Object.assign({}, equipment), { canRemove: true })], (eq) => eq._id);
                return {
                    equipments,
                };
            });
        };
        this.handleSave = () => {
            const { isCarrier, carrierPartner } = this.props;
            const { shipment, shipment: { receivables, payables }, stop, selectedReceivables, selectedPayables, selectedUnmappedAccessorialRequests, equipments, hasDrayageTrailerMovesTag, } = this.state;
            const container = findContainer(equipments);
            const trailer = findTrailer(equipments);
            const fallbackEquipment = hasDrayageTrailerMovesTag ? trailer : container;
            const equipmentList = uniqBy(equipments, (eq) => eq._id) || [];
            const changes = {
                equipments: equipmentList,
                accessorials: fallbackEquipment
                    ? selectedUnmappedAccessorialRequests.map((acc) => ({
                        _id: acc,
                        equipment: [fallbackEquipment],
                    }))
                    : [],
            };
            if (!isCarrier) {
                const adjustedReceivables = this.handleAdjustmentChange(receivables, selectedReceivables);
                const adjustedPayables = this.handleAdjustmentChange(payables, selectedPayables);
                changes.receivables = adjustedReceivables;
                changes.payables = adjustedPayables;
            }
            this.setState({ saving: true }, async () => {
                try {
                    const { entity: { job }, } = await updateShipmentStopEquipment(shipment, stop, changes, isCarrier, carrierPartner);
                    this.setState((prevState) => (Object.assign(Object.assign({}, prevState), getInitialState())), () => {
                        this.props.onHide();
                        // Need to check for duplicates before saving
                        this.props.onStopEquipmentUpdateSuccess({ job });
                    });
                }
                catch (e) {
                    this.props.onStopEquipmentUpdateFail(e);
                }
            });
        };
        this.state = Object.assign(Object.assign({}, getInitialState(props)), { shipment: {}, stop: {}, loaded: false });
    }
    componentDidMount() {
        EquipmentStore.addChangeListener(this.getCreatedEquipment);
        this.fetchShipment();
    }
    componentWillUnmount() {
        EquipmentStore.removeChangeListener(this.getCreatedEquipment);
    }
    disabled() {
        const { showAddNewEquipmentForm, saving } = this.state;
        return showAddNewEquipmentForm || saving;
    }
    // Create equipment using form
    async createEquipment(equipment) {
        try {
            const { entity: equipmentData } = await checkEquipmentExists(equipment);
            if (equipmentData.exists === true) {
                this.setState({ addNewEquipmentError: 'EQUIPMENT_ALREADY_EXIST' });
            }
            if (equipmentData.exists === false) {
                this.setState({ addNewEquipmentError: null }, () => EquipmentActionCreators.addEquipment(equipment));
            }
        }
        catch (e) {
            if (sluggify(e.entity.message) === 'EQUIPMENT_MISMATCH') {
                this.setState({ addNewEquipmentError: 'MISMATCHED_EQUIPMENT_TYPE' });
            }
        }
    }
    handleAdjustmentChange(current, updates) {
        if (updates && updates.length) {
            const { equipments, hasDrayageTrailerMovesTag } = this.state;
            const container = findContainer(equipments);
            const trailer = findTrailer(equipments);
            const fallbackEquipment = hasDrayageTrailerMovesTag ? trailer : container;
            updates.forEach((update_id) => {
                // Allow for the potential of duplicate _id under a ledger. Brought up due to multi-invoice migration 03/21
                const items = current.filter((item) => item._id === update_id);
                if (items) {
                    items.forEach((item) => {
                        item.equipment = item.equipment
                            ? item.equipment
                            : fallbackEquipment;
                    });
                }
                else {
                    console.log(`Did not find id ${update_id} in current list`);
                }
            });
        }
        return current;
    }
    renderEquipmentTable() {
        const { isCarrier, t } = this.props;
        const { equipments, availableEquipment, hasDrayageTrailerMovesTag } = this.state;
        if (equipments.length === 0 && availableEquipment.length === 0)
            return null;
        const showChargesForTrailerMovesTag = !hasDrayageTrailerMovesTag ||
            equipments.some(({ _equipmentType }) => _equipmentType === 'trailer');
        return (React.createElement("div", { className: "pt-4" },
            React.createElement("h4", { className: "my-0 mb-2" }, t('CURRENT_EQUIPMENT')),
            React.createElement("div", { className: "mb-4" },
                React.createElement(EquipmentTable, { equipments: this.state.equipments, availableEquipment: this.state.availableEquipment, shipmentId: this.props.shipmentId, showAvailability: !isCarrier, showActions: true, onRemove: this.handleEquipmentRemove, onAdd: this.handleEquipmentSelect })),
            showChargesForTrailerMovesTag && this.renderOrphanedAdjustments()));
    }
    renderOrphanedAdjustments() {
        const { isCarrier } = this.props;
        if (isCarrier) {
            return null;
        }
        const { shipment: { receivables, payables }, showAddNewEquipmentForm, selectedPayables, selectedReceivables, unmappedAccessorialRequests, selectedUnmappedAccessorialRequests, saving } = this.state;
        if (showAddNewEquipmentForm) {
            return null;
        }
        const receivableOrphans = (receivables || []).filter((receivable) => !receivable.equipment);
        const payableOrphans = (payables || []).filter((payable) => !payable.equipment);
        const { equipments, hasDrayageTrailerMovesTag } = this.state;
        return (React.createElement("div", { className: "mt-4" }, ((hasDrayageTrailerMovesTag && trailerExists(equipments)) ||
            containerExists(equipments)) && !saving && (React.createElement(OrphanAdjustments, { equipments: equipments, receivables: receivableOrphans, payables: payableOrphans, unmappedAccessorialRequests: unmappedAccessorialRequests, onSelect: this.handleToggleOrphanedAdjustment, selectedPayables: selectedPayables, selectedReceivables: selectedReceivables, selectedUnmappedAccessorialRequests: selectedUnmappedAccessorialRequests }))));
    }
    renderAddEquipmentButton() {
        if (this.state.showAddNewEquipmentForm) {
            return null;
        }
        const { t } = this.props;
        return (React.createElement("div", { className: "pt-4" },
            React.createElement("div", { className: "flex space-x-1 items-center mb-2" },
                React.createElement("h4", { className: "my-0" }, t('ADD_NEW_EQUIPMENT')),
                React.createElement(Tooltip, { id: "equipment-not-found" }, t('ADD_NEW_EQUIPMENT_NOTE'))),
            React.createElement("button", { className: "button button-green", onClick: () => this.setState({ showAddNewEquipmentForm: true }) },
                React.createElement("div", { className: "flex items-center space-x-1" },
                    React.createElement(PlusIcon, { className: "w-5 h-5" }),
                    React.createElement("span", null, t('NEW_EQUIPMENT'))))));
    }
    renderEquipmentSearch() {
        if (this.state.showAddNewEquipmentForm) {
            return null;
        }
        const { t } = this.props;
        return (React.createElement("div", { className: "pb-4" },
            React.createElement("div", { className: "flex items-center mb-2 space-x-1" },
                React.createElement("h4", { className: "my-0" }, t('EQUIPMENT_SEARCH')),
                React.createElement(Tooltip, { className: "text-sm", id: "equipment-number" }, t('EQUIPMENT_SEARCH_NOTE'))),
            React.createElement(EquipmentSearch, { onEquipmentSelect: (e) => this.handleEquipmentSearchSelect(e), onSearchChange: (equipmentSearchText) => this.setState({ equipmentSearchText }) })));
    }
    renderAddEquipmentForm() {
        const { t } = this.props;
        const { addNewEquipmentError, showAddNewEquipmentForm, equipmentSearchText, } = this.state;
        if (!showAddNewEquipmentForm) {
            return null;
        }
        return (React.createElement("div", { className: "" },
            addNewEquipmentError && (React.createElement(Alert, { title: t('ERRORS'), type: "error" },
                React.createElement("ul", null,
                    React.createElement("li", { key: 1 }, t(addNewEquipmentError))))),
            React.createElement(AddEquipmentForm, { equipmentId: equipmentSearchText, onSave: (equipment) => this.createEquipment(equipment), onCancel: () => this.setState({ showAddNewEquipmentForm: false }) })));
    }
    renderBody() {
        if (!this.state.loaded) {
            return React.createElement(LoadingCircle, { size: 2 });
        }
        const { t } = this.props;
        return (React.createElement("div", null,
            this.state.error && (React.createElement(Alert, { type: "error", title: t('ERROR'), className: "mb-4" })),
            React.createElement("div", { className: "divide-y divide-solid divide-gray-200" },
                this.renderEquipmentSearch(),
                this.renderEquipmentTable(),
                this.renderAddEquipmentButton(),
                this.renderAddEquipmentForm())));
    }
    render() {
        return (React.createElement(CargomaticModal, { show: this.props.show, onHide: () => this.props.onHide(), backdrop: "static", title: React.createElement(StopEquipmentModalTitle, null), showCloseButton: true, footer: React.createElement(StopEquipmentModalFooter, { cancelDisabled: this.state.saving, saveDisabled: this.disabled(), onCancel: () => this.props.onHide(), onSave: this.handleSave }) }, this.renderBody()));
    }
}
StopEquipmentModal.propTypes = {
    shipmentId: PropTypes.string.isRequired,
    stopId: PropTypes.string.isRequired,
    show: PropTypes.bool.isRequired,
    onStopEquipmentUpdateSuccess: PropTypes.func,
    onStopEquipmentUpdateFail: PropTypes.func,
    onHide: PropTypes.func.isRequired,
    isCarrier: PropTypes.bool,
    carrierPartner: PropTypes.string,
};
StopEquipmentModal.defaultProps = {
    onStopEquipmentUpdateSuccess: () => { },
    onStopEquipmentUpdateFail: () => { },
    isCarrier: true,
};
export default withTranslation()(StopEquipmentModal);
